import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ViewBundleOffer from 'components/OfferList/OfferListTiles/SearchOfferTiles/ViewBundleOffer'
import HotelSearchTileAlternativeDatesBanner from 'components/SearchKit/SearchTiles/accommodations/HotelSearchTileAlternativeDatesBanner'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, PRODUCT_TYPE_LIMITED_TIME_SPECIAL } from 'constants/offer'
import useOffer from 'hooks/Offers/useOffer'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import { nonNullable } from 'lib/array/arrayUtils'
import { getPackageUniqueKey, isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import { logNewRelic } from 'services/newRelic'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import ExperimentalHotelSearchTileFeatures from './ExperimentalHotelSearchTileFeatures'
import HotelSearchTileAction from './HotelSearchTileAction'
import HotelSearchTileEarnablePoints from './HotelSearchTileEarnablePoints'
import HotelSearchTileFeatures from './HotelSearchTileFeatures'
import HotelSearchTileHighlightBanner from './HotelSearchTileHighlightBanner'
import HotelSearchTileLabels from './HotelSearchTileLabels'
import useMetaSearchDistance from './useMetaSearchDistance'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'
import HotelSearchTileUnavailabilityBanner from './HotelSearchTileUnavailabilityBanner'
import HotelSearchTileCharterFareClassBanner from './HotelSearchTileCharterFareClassBanner'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SearchTileComparisonCheckbox from 'components/SearchKit/SearchTiles/shared/SearchTileComparisonCheckbox'
import { diffInDays } from 'lib/datetime/dateUtils'
import { generalLog } from 'marketing/api'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import LoyaltyStayUpgradeBenefitMessageBanner from 'loyalty/components/LoyaltyStayUpgradeBenefitMessageBanner'
import useOfferRating from 'hooks/Offers/useOfferRating'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { CHARTER_PAGE_SLUG, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import Group from 'components/utils/Group'
import useSearchTargetName from 'hooks/Search/useSearchTargetName'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  filters?: App.OfferListFilters
  showPerNightPricing?: boolean
  storeSearchTile?: boolean
  showDescription?: boolean
  position?: number
}

function HotelSearchTile({ offer, filters, showPerNightPricing = false, storeSearchTile = false, showDescription = false, position }: Props) {
  const intl = useIntl()
  const { maldivesBundleLabel } = useCharterFlightLabels()
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters?.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const isSpecificSearch = !!(checkIn && checkOut && filters?.rooms)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)

  const [, fetchingOffer] = useOffer<App.Offer>(offer.id, {
    requireSummaryOnly: !isSpecificSearch,
  })

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration

  const hasFiltersSet = !!(checkIn && checkOut && filters?.rooms)

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage && fullPath.includes('metasearch')) {
        const priceTotal = queryParams.priceTotal || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceTotal) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          logNewRelic('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      // use search pricing package id by default

      if (!pkgId || !duration || !bestMaybeAvailableRate?.rate?.roomRateId) {
        return offer.lowestPricePackage
      }

      const pkg = offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, bestMaybeAvailableRate.rate.roomRateId))
      return pkg ?? offer.lowestPricePackage
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const correctBestPackage = useMemo<App.HotelPackage | undefined>(() => {
    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage) {
        const priceShown = queryParams.priceShown || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceShown) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          logNewRelic('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      // use search pricing package id by default
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      const roomRateId = metaData?.pricing?.lowestPriceRoomRateId ?? bestMaybeAvailableRate?.rate?.roomRateId

      if (!pkgId || !duration || !roomRateId) {
        return
      }

      return offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, roomRateId))
    }
  }, [shouldUseRealSearchPrices, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const productType = (isLPCTacticalEnabled() && offer.hasTactical) ? PRODUCT_TYPE_LIMITED_TIME_SPECIAL : offer.productType

  const locations = useMemo<Array<string>>(() => nonNullable([offer.locationHeading, offer.locationSubheading]), [offer])
  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(storeSearchTile, offer, filters, mapSearchPath)
  const distanceFromSearchTarget = metaData?.distance
  const searchTargetName = useSearchTargetName(filters)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const showProductLabel = isSpoofed || productType !== OFFER_TYPE_ALWAYS_ON

  // Maldives Direct charter bundles should use calendar-based availability (multi-room allocation),
  // since offerBestPrices can incorrectly report unavailable for the selected occupancy.
  const { charterBundle: isMaldivesDirectCharterBundleForSoldOut } = useMaldivesDirectBundleContext()
  const { pricingByOfferId: pricingByOfferIdForSoldOut, isLoading: isMaldivesPricingLoadingForSoldOut } = useMaldivesOffersPricingContext()
  const contextPricingForSoldOut = pricingByOfferIdForSoldOut[offer.id]

  const isSoldOutFromSearch = shouldUseRealSearchPrices
    ? !metaData?.available
    : (hasFiltersSet && bestMaybeAvailableRate !== undefined && !bestMaybeAvailableRate.available)

  const isSoldOut = (isMaldivesDirectCharterBundleForSoldOut &&
    !isMaldivesPricingLoadingForSoldOut &&
    contextPricingForSoldOut) ?
      !contextPricingForSoldOut.hasAvailability :
    isSoldOutFromSearch

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        availability: false,
        price: undefined,
        roomRateId: undefined,
        memberPrice: undefined,
        duration: undefined,
        extendedAvailability: metaData?.extendedAvailability,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const showSpoofHighlight = useMemo<boolean>(() => {
    return isSpoofed && !scheduleIsCurrent(offer.visibilitySchedule)
  }, [isSpoofed, offer.visibilitySchedule])

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights

  const showHighlight = useMemo(() => {
    if (showSpoofHighlight) return true
    if (showDescription) return false

    if (!filters?.propertyId || filters?.searchNearby) return false
    return offer.property?.id === filters.propertyId
  }, [filters, offer, showSpoofHighlight, showDescription])
  const showSpecificRoomRateAndRoomType = filters?.roomRateId && filters?.roomTypeId
  const bestAvailableRate = bestMaybeAvailableRate?.available && !showSpecificRoomRateAndRoomType ? bestMaybeAvailableRate.rate : undefined
  const { defaultPricing: tileDisplayPricing } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration: duration ?? 1,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })
  const hidePrice = useHideAnytimePrices(offer.type, metaData?.hidePricing)
  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const currency = useContext(CurrencyContext)
  const rating = useOfferRating(offer, metaData, fullPath.includes('metasearch'))
  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const isUpgradeAvailableForSelectedDates = isAnytimeSearch ? true : (metaData?.packagesWithAvailableUpgrade.length ?? 0) > 0

  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  const showTasteOfGoldBenefit =
    isTasteOfGoldActive &&
    isTasteOfGoldEligibleOffer(offer, currency, offer.property.geoData.countryCode?.toLowerCase() !== 'au', tileDisplayPricing.price)

  const isMaldivesCharter = useAppSelector((state: App.State) => getIsSpecificCampaignPage(state, CHARTER_PAGE_SLUG))

  // Check if this is a charter bundle offer by checking campaignTags
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])

  const hotelFlightBundleLabel = intl.formatMessage({
    id: 'PjmdhR5JAQXM7v4WOevW',
    defaultMessage: 'Hotel & Flight bundle',
    description: 'Label for charter bundle offers combining hotel and flight',
  })
  const isFlashHotelProductType = productType === OFFER_TYPE_HOTEL

  const productTypeLabelNode = useMemo<React.ReactNode>(() => {
    if (isCharterBundleOffer) {
      return <StructureLabel variant="maldives-charter">{hotelFlightBundleLabel}</StructureLabel>
    }
    if (isMaldivesCharter) {
      return null
    }
    if (!isFlashHotelProductType && !showProductLabel) {
      return null
    }

    const label = <ProductTypeLabel productType={productType} />

    if (isFlashHotelProductType) {
      return (
        <LtleTooltipWrapper autoOpenOnce>
          {label}
        </LtleTooltipWrapper>
      )
    }

    return label
  }, [isCharterBundleOffer, isMaldivesCharter, isFlashHotelProductType, showProductLabel, productType, hotelFlightBundleLabel])

  const handleSelectDatesClick = useCallback(() => {
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })

  const comparisonPriceTotals = useMemo(() => {
    if (!bestPackage) return undefined
    return { price: bestPackage.price, memberPrice: bestPackage.memberPrice, value: bestPackage.value }
  }, [bestPackage])

  const shouldAppendFlights = isCharterBundleOffer && !offer.property.name.toLocaleLowerCase().includes(' & flights')
  const offerTitle = shouldAppendFlights
    ? intl.formatMessage(
        {
          id: 'jlWVmAHHGkjvRL3MaCvy',
          defaultMessage: '{propertyName} & Flights',
          description: 'Hotel search tile title appended with flights bundle suffix for charter offers',
        },
        { propertyName: offer.property.name },
      )
    : offer.property.name

  return <SearchTile
    offer={offer}
    state={showHighlight ? 'highlighted' : 'default'}
    action={!isSoldOut && <HotelSearchTileAction offer={offer} offerMetaData={metaData} onSelectDatesClick={handleSelectDatesClick} />}
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {showHighlight && <HotelSearchTileHighlightBanner offer={offer} offerMetaData={metaData} filters={filters} />}
      <HotelSearchTileCharterFareClassBanner offerId={offer.id} />
      {offerSiteTakeover?.searchPage?.tileBanner}
    </>}
    bookmarkAction={storeSearchTile ? undefined : <BookmarkButton offer={offer} withLabel="always" />}
    cancellationPolicyLabel={(!noGoogleAffiliations || isFlashHotelOffer(offer)) && !!cancellationPolicyType && <TileCancellationPolicy
      cancellationPolicyType={cancellationPolicyType}
      checkInDate={filters?.checkIn}
      timezoneOffset={offer.property.timezoneOffset}
      offerType={offer.type}
    />}
    bottomExtension={<>
      {config.ENABLE_BUNDLE_AND_SAVE && metaData?.bundleOfferId && <ViewBundleOffer
        offer={offer}
        filters={filters}
        offerMetaData={metaData}
        offerLinkIncludesFilters
      />}
      {metaData?.extendedAvailability && <HotelSearchTileAlternativeDatesBanner
        checkIn={metaData.extendedAvailability.checkIn}
        checkOut={metaData.extendedAvailability.checkOut}
      />}
      {isSoldOut && !metaData?.extendedAvailability &&
        <HotelSearchTileUnavailabilityBanner
          offer={offer}
          unavailableReason={isMaldivesDirectCharterBundleForSoldOut ? contextPricingForSoldOut?.unavailableReason ?? metaData?.unavailableReason : metaData?.unavailableReason}
          filters={filters}
        />}
    </>}
    topExtension={
      (hasUpgradablePackages && hasRoomUpgradesAvailable) &&
      <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} upgradeNotAvailableForSelectedDates={!isUpgradeAvailableForSelectedDates} />
    }
    features={<>
      {!noGoogleAffiliations || isFlashHotelOffer(offer) ?
          <HotelSearchTileFeatures offer={offer} offerMetaData={metaData} filters={filters} bestPackage={bestPackage} doNotOpenInclusionsModal={storeSearchTile} /> :
          <ExperimentalHotelSearchTileFeatures offer={offer} offerMetaData={metaData} filters={filters} bestPackage={correctBestPackage} />}
    </>}
    images={offer.images}
    labels={
      <HotelSearchTileLabels
        offer={offer}
        filters={filters}
        bestPackage={bestPackage}
        showUrgencyTags={!isSoldOut}
      />
    }
    overline={<SearchTileRowOverlineLocation
      locations={locations}
      mapUrl={isCharterBundleOffer || isMaldivesCharter ? undefined : mapUrl}
      distance={distanceFromSearchTarget}
      searchTargetLabel={searchTargetName}
    />}
    pricePoint={!isSoldOut && <HotelSearchTilePriceStack
      offer={offer}
      filters={filters}
      bestPackage={bestPackage}
      duration={duration ?? 1}
      bestRate={bestAvailableRate}
      hidePrice={hidePrice}
      showPerNightPricing={showPerNightPricing}
      hideIncludesTaxesAndFees={storeSearchTile}
    />}
    productLabel={<LabelGroup>
      {productTypeLabelNode}
      <OfferPartnershipLabel offer={offer} />
      {isMaldivesCharter && !isCharterBundleOffer && <Label kind="primary" variant="maldives-charter">{maldivesBundleLabel}</Label>}
      {filters?.showDistanceWithMetaOfferId && metaSearchDistanceToMainOffer && <PromotionLabel variant="default">{metaSearchDistanceToMainOffer}</PromotionLabel>}
    </LabelGroup>}
    rating={rating}
    title={offerTitle}
    meta={!storeSearchTile && <Group direction="vertical" gap={8}>
      <HotelSearchTileEarnablePoints
        offer={offer}
        bestPackage={bestPackage}
        bestAvailableRate={bestAvailableRate}
        filters={filters}
        metaData={metaData}
      />
      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
    </Group>}
    comparisonCheckbox={<SearchTileComparisonCheckbox
      offerType={offer.type}
      offerId={offer.id}
      name={offer.property.name}
      filters={filters}
      priceTotals={comparisonPriceTotals}
    />}
  />
}

export default HotelSearchTile
